import { useCallback, useEffect, useRef } from 'react';
import type { DataTableParams } from '@/shared/ui/data-table';
import { applyQuery, countBy } from './query';
import type { SearchFields } from './query';
import { isForbidden } from './errors';

/**
 * The one `onFetch` handler the agreements lists share.
 *
 * WHY THIS EXISTS
 * ---------------
 * All three agreements lists answer the engine's query the same way: read the whole
 * collection once, narrow it locally, and derive the saved-view counts from the SAME
 * read. Written three times it produced three near-identical handlers, and the
 * differences that crept in were the kind nobody notices — one page counted its tabs
 * from the filtered rows, another from the whole collection.
 *
 * WHAT IT GUARANTEES
 * ------------------
 *  - **One read per question.** The page asks the first query before the table exists,
 *    and the table announces that same query when it mounts. The handler answers the
 *    first and ignores the echo, so the collection is read once.
 *  - **Counts travel with the rows.** The counts are derived from the same read that
 *    answered the query, so a failed read is the page's error surface rather than a
 *    tab strip of zeros.
 *  - **A refusal is not a failure.** A 403 settles the page as `denied`, which the
 *    layout renders as a refusal — never as an empty list, which would be
 *    indistinguishable from a deployment with no records.
 */

/** The four states a list read can settle into. */
export type ListPhase = 'loading' | 'ready' | 'error' | 'denied';

/** What the handler needs from the page to report a read's outcome. */
export type ListFetchSink<T> = {
  readonly setRows: (rows: T[]) => void;
  readonly setTotal: (total: number) => void;
  readonly setCounts: (counts: Partial<Record<string, number>>) => void;
  readonly setPhase: (phase: ListPhase) => void;
  /**
   * The failure, in the provider's own words.
   *
   * The page reports what the server said, never a reason of its own invention — a
   * generic "something went wrong" would hide the one sentence that tells the
   * operator whether to retry, wait, or call someone.
   */
  readonly setError: (error: Error | null) => void;
};

/** The handler the page hands to `AppListLayout` as `onFetch`. */
export type ListFetchHandler = (
  params: DataTableParams,
  options?: { force?: boolean },
) => void;

/** Stable identity for one query, so the same question is never read twice. */
function queryKey(params: DataTableParams): string {
  return JSON.stringify([params.page, params.pageSize, params.sort, params.filters]);
}

/**
 * Builds the `onFetch` handler for one agreements list.
 *
 * `read` is the whole-collection read; `countKeyOf` names the field a saved view counts
 * by, so a tab's badge is a claim about the collection rather than about the page.
 */
export function useListFetch<T>(
  read: () => Promise<ReadonlyArray<T>>,
  searchFields: SearchFields<T>,
  countKeyOf: (row: T) => string,
  sink: ListFetchSink<T>,
): ListFetchHandler {
  const answered = useRef<string | null>(null);
  const accumulated = useRef<T[]>([]);
  const { setRows, setTotal, setCounts, setPhase, setError } = sink;

  /*
   * The read and the two field selectors are held in refs, not in the callback's
   * dependency list.
   *
   * They are read when the query is ANSWERED, not when the handler is built, so
   * their identity is not part of the handler's meaning. Depending on them made
   * the handler a new function on every render for any page that wrote
   * `countKeyOf={(row) => ...}` inline — and the list re-reads whenever the
   * handler changes, so the page read, set state, re-rendered, built a new
   * handler, and read again. A page whose read FAILED looped forever, because
   * the failure path also sets state.
   *
   * The refs are written in an effect, not during render: a ref written during
   * render is a value React cannot see change, and the lint rule is right to
   * refuse it. The effect runs before any query can be answered, because a query
   * is only answered from an event or an effect, both of which run after it.
   *
   * The setters are stable `useState` setters, so they stay in the dependency
   * list where they belong.
   */
  const readRef = useRef(read);
  const searchFieldsRef = useRef(searchFields);
  const countKeyOfRef = useRef(countKeyOf);
  useEffect(() => {
    readRef.current = read;
    searchFieldsRef.current = searchFields;
    countKeyOfRef.current = countKeyOf;
  }, [read, searchFields, countKeyOf]);

  return useCallback(
    (params: DataTableParams, options?: { force?: boolean }): void => {
      const key = queryKey(params);
      // The page asks the first query before the table exists; the table announces
      // that same query when it mounts. One question, one read. A refresh is the one
      // caller that may ask the same question again on purpose.
      if (!options?.force && answered.current === key) return;
      answered.current = key;

      readRef.current()
        .then((all) => {
          const { data: page, total: matched } = applyQuery(
            all,
            params,
            searchFieldsRef.current,
          );

          /*
           * `append` is the table telling us this page CONTINUES the list on screen
           * rather than replacing it — which is what the card view's scroll asks for.
           * Without it the grid would show only the newest page and the cards above
           * would vanish as the user scrolled. The table view's pager does not set it,
           * so paging still replaces.
           */
          accumulated.current = params.append ? [...accumulated.current, ...page] : page;

          setRows(accumulated.current);
          setTotal(matched);
          setCounts({ all: all.length, ...countBy(all, countKeyOfRef.current) });
          setError(null);
          setPhase('ready');
        })
        .catch((error: unknown) => {
          // A refusal is not a failure. The read was not allowed, so the page shows
          // the refusal surface rather than an error the operator could retry.
          if (isForbidden(error instanceof Error ? error : null)) {
            setPhase('denied');
            return;
          }
          console.error(error);
          setError(error instanceof Error ? error : new Error(String(error)));
          setPhase('error');
        });
    },
    [setRows, setTotal, setCounts, setPhase, setError],
  );
}
